// Which way a walking sprite faces (the trainer and the creatures, on the hunt field and in the corner window).
// Picking the larger of |dx| and |dy| every frame makes a sprite on a diagonal, or on a path that steps across and
// down in turn, flip between its side view and its front or back view every few frames. So the heading is smoothed
// over a fraction of a second, and the facing only turns to the other axis once that axis clearly leads.

export type Dir = 'down' | 'up' | 'left' | 'right';

/** How far the other axis must lead before the facing turns to it: 1.5 holds a facing to about 56 degrees off its axis. */
const HOLD = 1.5;
/** Smoothing time of the heading, in ms. */
const SMOOTH_MS = 120;

/** A smoothed direction of travel (about unit length once moving). */
export interface Heading { vx: number; vy: number }
export const heading = (): Heading => ({ vx: 0, vy: 0 });

/** Blend a step (dx, dy) taken over dtMs into the heading. A step that doubles back turns the heading at once. */
export function steer(h: Heading, dx: number, dy: number, dtMs: number): Heading {
  const d = Math.hypot(dx, dy);
  if (d > 0) {
    const ux = dx / d, uy = dy / d;
    const k = ux * h.vx + uy * h.vy < 0 ? 1 : 1 - Math.exp(-Math.max(0, dtMs) / SMOOTH_MS);
    h.vx += (ux - h.vx) * k;
    h.vy += (uy - h.vy) * k;
  }
  return h;
}

/** The facing for a move (dx, dy): it stays on the current facing's axis unless the other axis clearly leads. */
export function holdDir(cur: Dir, dx: number, dy: number): Dir {
  const ax = Math.abs(dx), ay = Math.abs(dy);
  if (!ax && !ay) return cur;
  const side = cur === 'left' || cur === 'right' ? ax * HOLD >= ay : ax > ay * HOLD;
  return side ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up';
}

/** The same for a creature sheet's facings (front = down, back = up). */
type Face = 'front' | 'back' | 'left' | 'right';
const TO_DIR: Record<Face, Dir> = { front: 'down', back: 'up', left: 'left', right: 'right' };
const TO_FACE: Record<Dir, Face> = { down: 'front', up: 'back', left: 'left', right: 'right' };
export const holdFace = (cur: Face, dx: number, dy: number): Face => TO_FACE[holdDir(TO_DIR[cur], dx, dy)];
